import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { TextField } from '@mui/material'

import { useUpdateMemoMutation } from '@/features/yakureki/hooks/queries/useUpdateMemoMutation'

// 未保存分を確実に永続化してから、確定した memo_items を返す.
// 「メモを読む側」(= メモから SOAP 生成) が保存の着地を待つための口.
export type MemoFlush = () => Promise<string[]>

interface MemoPanelProps {
  yakurekiId: string
  // 服薬指導前 / 中 / 後 で共通に編集する覚書. PATCH /update-memo/ で永続化.
  memoItems: string[]
  // mount 中だけ flush 関数を載せる箱. caller (= page) が保存を待ち合わせるのに使う.
  flushRef?: MutableRefObject<MemoFlush | null>
  // mount した時点で書き足せる状態にする (focus して caret を末尾へ). 管理栄養士のカルテで、
  // メモを書き始めた 1 手で記録が生まれて画面が替わったとき、書いていた続きから打てるように.
  autoFocus?: boolean
}

const AUTOSAVE_DEBOUNCE_MS = 1500

const itemsToText = (items: string[]): string => items.join('\n')

export const textToItems = (text: string): string[] =>
  text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

// 服薬指導メモ panel.
//
// 内部は string[] (= memo_items) だが、 UI は freeform multiline テキストエリア 1 つに統一する.
// 行 = 1 item として保存時に分解、 表示時は join('\n') で連結する.
//
// 保存のレバーは 3 つ:
//   1. 1500ms inactivity の debounce
//   2. blur (= タブ切替 / 他要素クリック)
//   3. unmount (= 患者切替で iframe ごと再マウントされる等. blur を伴わずに消える経路)
// 加えて flushRef 経由で外から「確実に保存して着地を待つ」ことができる.
export const MemoPanel: FC<MemoPanelProps> = ({
  yakurekiId,
  memoItems: initialItems,
  flushRef,
  autoFocus = false,
}) => {
  const [text, setText] = useState<string>(() => itemsToText(initialItems))
  // 直前に save 済みのテキスト. 重複 save 抑制 + server からの再 fetch との同期判定に使う.
  const lastSavedRef = useRef<string>(itemsToText(initialItems))
  // 最新の text を effect の cleanup / flush から参照する (= 依存に text を入れずに済ませる).
  const textRef = useRef<string>(text)
  textRef.current = text
  // 飛行中の保存. flush はこれを await して「back が最新を読める」状態を保証する.
  const inFlightRef = useRef<Promise<unknown> | null>(null)
  const updateMemoMutation = useUpdateMemoMutation()

  const save = useCallback(
    (value: string) => {
      if (value === lastSavedRef.current) return
      lastSavedRef.current = value
      // 楽観的に lastSaved を進めるため、着地の追跡は promise 側で持つ.
      // ここで進めないと debounce と blur で同じ内容を二重送信する.
      const pending = updateMemoMutation
        .mutateAsync({ yakurekiId, memoItems: textToItems(value) })
        .catch(() => {
          // 失敗 toast は mutation の onError が出す. ここで握らないと unhandled rejection になる.
        })
      inFlightRef.current = pending
      void pending.finally(() => {
        if (inFlightRef.current === pending) inFlightRef.current = null
      })
    },
    [yakurekiId, updateMemoMutation]
  )

  // 外から呼ぶ用. 未保存分を送り、飛行中の保存があれば着地まで待つ.
  const flush = useCallback(async (): Promise<string[]> => {
    save(textRef.current)
    await inFlightRef.current
    return textToItems(textRef.current)
  }, [save])

  useEffect(() => {
    if (!flushRef) return
    flushRef.current = flush
    return () => {
      flushRef.current = null
    }
  }, [flushRef, flush])

  // unmount 時の取りこぼし防止. debounce の cleanup はタイマーを消すだけなので、
  // 「入力 → 1500ms 以内に blur を伴わず消える」と最後の編集が失われる.
  // AnyCOMPASS では患者を切り替えると親が KakeruDrawer を再マウントし iframe ごと
  // 作り直すため、これは現実に起きる. mutate は QueryClient 側に載るので component が
  // 消えても送信は続く.
  const saveOnUnmountRef = useRef<() => void>(() => undefined)
  saveOnUnmountRef.current = () => save(textRef.current)
  useEffect(() => () => saveOnUnmountRef.current(), [])

  // server 側の memo_items が更新されたら反映. ただし編集中 (= 未保存差分あり) は上書きしない.
  useEffect(() => {
    const next = itemsToText(initialItems)
    if (text === lastSavedRef.current) {
      setText(next)
      lastSavedRef.current = next
    }
    // text への依存を入れると毎タイプで効果が走り user 入力を消す危険. eslint-disable で明示的に切る.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems])

  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  useEffect(() => {
    const el = inputRef.current
    if (!autoFocus || !el) return
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
    // mount した時だけ (後から true になっても奪わない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // インアクティビティ debounce. 1500ms タイプが止まれば auto-save.
  useEffect(() => {
    if (text === lastSavedRef.current) return
    const id = window.setTimeout(() => save(text), AUTOSAVE_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [text, save])

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      maxRows={20}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => save(text)}
      placeholder="指導メモを書く"
      variant="standard"
      inputRef={inputRef}
      InputProps={{
        disableUnderline: true,
        sx: {
          // body2 variant 参照で文字拡大 (large) に追従させる (scaleVariant 済の値).
          fontSize: (theme) => theme.typography.body2.fontSize,
          lineHeight: 1.7,
          alignItems: 'flex-start',
          p: 0,
        },
      }}
    />
  )
}
